import { useEffect, useState } from "react";
import { Plus, X, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";

export default function Moderation() {
  const [data, setData] = useState(null);
  const [banForm, setBanForm] = useState({ user_id: "", reason: "" });
  const [blForm, setBlForm] = useState({ guild_id: "", reason: "" });

  const load = () => api.get("/admin/moderation").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (kind, body, reset) => {
    try {
      await api.post(`/admin/moderation/${kind}`, body);
      toast.success("Entry added");
      reset();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (kind, key, value) => {
    await api.delete(`/admin/moderation/${kind}`, { data: { [key]: value } });
    toast.success("Entry removed");
    load();
  };

  const setReportStatus = async (rid, status) => {
    await api.patch(`/admin/moderation/reports/${rid}`, { status });
    toast.success(`Report ${status}`);
    load();
  };

  if (!data) return <p className="text-white/40 font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-moderation">
      <PageTitle title="Moderation" sub="Bans, blacklists and open reports — keyed by Discord IDs so renames can't dodge them." />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="mod-bans">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Banned users</p>
          <div className="space-y-2 mb-4">
            {data.banned_users.map((b) => (
              <div key={b.user_id || b.username} className="flex items-center justify-between rounded-md bg-white/[0.03] border border-white/5 px-3 py-2.5">
                <div>
                  <p className="text-sm text-white font-medium font-mono">{b.user_id || b.username}</p>
                  <p className="text-xs text-white/40">{b.reason}</p>
                </div>
                <button onClick={() => remove("banned_users", "user_id", b.user_id || b.username)} className="text-white/30 hover:text-emerald-400 transition-colors duration-150" data-testid={`unban-${b.user_id || b.username}`} aria-label="Unban"><X size={14} /></button>
              </div>
            ))}
            {!data.banned_users.length && <p className="text-xs text-white/30">None.</p>}
          </div>
          <div className="space-y-2">
            <input value={banForm.user_id} onChange={(e) => setBanForm({ ...banForm, user_id: e.target.value.replace(/\D/g, "") })} placeholder="Discord user ID (numbers only)" data-testid="ban-user-id"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono focus:outline-none focus:ring-2 focus:ring-blurple" />
            <input value={banForm.reason} onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })} placeholder="Reason" data-testid="ban-reason"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
            <button onClick={() => banForm.user_id.length >= 15 && add("banned_users", banForm, () => setBanForm({ user_id: "", reason: "" }))} data-testid="ban-add"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-500/80 hover:bg-red-500 text-white text-sm font-semibold py-2 rounded-md transition-colors duration-150"><Plus size={13} /> Ban user</button>
            <p className="text-[11px] text-white/30">Right-click a user in Discord (developer mode) → Copy User ID.</p>
          </div>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="mod-blacklist">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Blacklisted servers</p>
          <div className="space-y-2 mb-4">
            {data.blacklisted_servers.map((s) => (
              <div key={s.guild_id || s.name} className="flex items-center justify-between rounded-md bg-white/[0.03] border border-white/5 px-3 py-2.5">
                <div>
                  <p className="text-sm text-white font-medium font-mono">{s.guild_id || s.name}</p>
                  <p className="text-xs text-white/40">{s.reason}</p>
                </div>
                <button onClick={() => remove("blacklisted_servers", "guild_id", s.guild_id || s.name)} className="text-white/30 hover:text-emerald-400 transition-colors duration-150" data-testid={`unblacklist-${s.guild_id || s.name}`} aria-label="Remove blacklist"><X size={14} /></button>
              </div>
            ))}
            {!data.blacklisted_servers.length && <p className="text-xs text-white/30">None.</p>}
          </div>
          <div className="space-y-2">
            <input value={blForm.guild_id} onChange={(e) => setBlForm({ ...blForm, guild_id: e.target.value.replace(/\D/g, "") })} placeholder="Discord server (guild) ID" data-testid="blacklist-guild-id"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono focus:outline-none focus:ring-2 focus:ring-blurple" />
            <input value={blForm.reason} onChange={(e) => setBlForm({ ...blForm, reason: e.target.value })} placeholder="Reason" data-testid="blacklist-reason"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
            <button onClick={() => blForm.guild_id.length >= 15 && add("blacklisted_servers", blForm, () => setBlForm({ guild_id: "", reason: "" }))} data-testid="blacklist-add"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-500/80 hover:bg-red-500 text-white text-sm font-semibold py-2 rounded-md transition-colors duration-150"><Plus size={13} /> Blacklist server</button>
            <p className="text-[11px] text-white/30">Right-click the server icon (developer mode) → Copy Server ID.</p>
          </div>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid="mod-reports">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4 flex items-center gap-2"><Flag size={13} className="text-amber-400" /> Reports</p>
          <div className="space-y-3">
            {data.reports.map((r) => (
              <div key={r.id} className="rounded-md bg-white/[0.03] border border-white/5 p-3.5" data-testid={`report-${r.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm text-white font-medium">{r.target} <span className="text-white/30 font-normal">({r.type})</span></p>
                  <Badge tone={r.status === "open" ? "amber" : "green"}>{r.status}</Badge>
                </div>
                <p className="text-xs text-white/50 mb-2.5">{r.reason} — reported by {r.reporter}</p>
                {r.status === "open" && (
                  <div className="flex gap-2">
                    <button onClick={() => setReportStatus(r.id, "resolved")} data-testid={`report-resolve-${r.id}`} className="text-xs font-semibold text-emerald-400 border border-emerald-400/30 rounded-full px-3 py-1 hover:bg-emerald-400/10 transition-colors duration-150">Resolve</button>
                    <button onClick={() => setReportStatus(r.id, "dismissed")} data-testid={`report-dismiss-${r.id}`} className="text-xs font-semibold text-white/40 border border-white/15 rounded-full px-3 py-1 hover:text-white transition-colors duration-150">Dismiss</button>
                  </div>
                )}
              </div>
            ))}
            {!data.reports.length && <p className="text-xs text-white/30">No reports.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
